import logger from '../utils/clientLogger';

// API Configuration
// Use direct URLs for both development and production
// Includes a compatibility rewrite for the legacy host.
const normalizeApiBaseUrl = (value) => {
  if (!value || typeof value !== 'string') return value;

  let url = value.trim();
  if (!url) return url;

  const legacyHost = ['social', 'loopino', 'ai'].join('.');
  const currentHost = ['loopino', 'ai'].join('.');

  // If old env/config still points to the legacy host, transparently migrate.
  try {
    const parsed = new URL(url);
    if (parsed.hostname?.toLowerCase() === legacyHost) {
      parsed.hostname = currentHost;
      url = parsed.toString();
    }
  } catch {
    if (url.startsWith('//')) {
      const host = url.slice(2).split('/')[0]?.toLowerCase();
      if (host === legacyHost) {
        url = `//${currentHost}${url.slice(2 + host.length)}`;
      }
    }
  }

  // Avoid double slashes when concatenating endpoints.
  url = url.replace(/\/+$/, '');

  return url;
};

const DEFAULT_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? (typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://loopino.ai')
    : 'http://localhost:5000';

const API_BASE_URL = normalizeApiBaseUrl(process.env.REACT_APP_API_URL || DEFAULT_BASE_URL);

logger.debug('API configuration loaded', {
  timestamp: new Date().toISOString(),
  reactAppApiUrl: process.env.REACT_APP_API_URL,
  apiBaseUrl: API_BASE_URL,
  nodeEnv: process.env.NODE_ENV
});

export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  ENDPOINTS: {
    AUTH: {
      LOGIN: '/api/v1/auth/login',
      GOOGLE: '/api/v1/auth/google',
      REGISTER: '/api/v1/auth/register',
      LOGOUT: '/api/v1/auth/logout',
      REFRESH: '/api/v1/auth/refresh',
      ME: '/api/v1/auth/me',
      FORGOT_PASSWORD: '/api/v1/auth/forgot-password',
      RESET_PASSWORD: '/api/v1/auth/reset-password',
      CHANGE_PASSWORD: '/api/v1/auth/change-password',
      VERIFY_EMAIL: '/api/v1/auth/verify-email'
    },
    COMPANY: {
      CREATE: '/api/v1/company/create',
      LIST: '/api/v1/company',
      GET: (id) => `/api/v1/company/${id}`,
      UPDATE: (id) => `/api/v1/company/${id}`,
      DELETE: (id) => `/api/v1/company/${id}`,
      UPLOAD: (id) => `/api/v1/company/${id}/upload`,
      ANALYZE: (id) => `/api/v1/company/${id}/analyze`
    },
    CAMPAIGNS: {
      LIST: '/api/v1/campaigns',
      CREATE: '/api/v1/campaigns',
      GET: (id) => `/api/v1/campaigns/${id}`,
      UPDATE: (id) => `/api/v1/campaigns/${id}`,
      DELETE: (id) => `/api/v1/campaigns/${id}`
    },
    POSTS: {
      LIST: '/api/v1/posts',
      GET: (id) => `/api/v1/posts/${id}`,
      UPDATE: (id) => `/api/v1/posts/${id}`,
      DELETE: (id) => `/api/v1/posts/${id}`,
      APPROVE: (id) => `/api/v1/posts/${id}/approve`,
      SCHEDULE: (id) => `/api/v1/posts/${id}/schedule`,
      BULK: (action) => `/api/v1/posts/bulk/${action}`
    },
    TRENDS: {
      RESEARCH: '/api/v1/trends/research',
      MONITOR: '/api/v1/trends/monitor',
      MONITOR_STATUS: (id) => `/api/v1/trends/monitor/${id}`,
      ALERTS: '/api/v1/trends/alerts',
      SUGGESTIONS: '/api/v1/trends/suggestions',
      APPLY: '/api/v1/trends/apply'
    },
    ANALYTICS: {
      OVERVIEW: '/api/v1/analytics/overview',
      CAMPAIGNS: (id) => `/api/v1/analytics/campaigns/${id}`,
      COMPARE: '/api/v1/analytics/campaigns/compare',
      ENGAGEMENT: '/api/v1/analytics/engagement',
      CONTENT_PERFORMANCE: '/api/v1/analytics/content-performance',
      REPORTS: {
        GENERATE: '/api/v1/analytics/reports/generate',
        STATUS: (id) => `/api/v1/analytics/reports/${id}/status`,
        DOWNLOAD: (id) => `/api/v1/analytics/reports/${id}/download`
      },
      REAL_TIME: '/api/v1/analytics/real-time',
      ALERTS: '/api/v1/analytics/alerts'
    },
    AI: {
      GENERATE_IMAGES: '/api/v1/ai/generate-images',
      GENERATE_IMAGES_ENHANCED: '/api/v1/ai/generate-images-enhanced',
      STRATEGY: (id) => `/api/v1/ai/${id}/strategy`,
      STRATEGY_WEEK: (id, weekIndex) => `/api/v1/ai/${id}/strategy/week/${weekIndex}`,
      POSTS: (id) => `/api/v1/ai/${id}/posts`,
      // Deprecated name: this endpoint now generates only the Company DNA (markdown).
      COMPANY_ANALYZE: (id) => `/api/v1/ai/company/${id}/dna`,
      COMPANY_ANALYZE_PROGRESS: (companyId, requestId) => `/api/v1/ai/company/${companyId}/analyze/progress/${requestId}`,
      COMPANY_ANALYZE_RESULT: (companyId, requestId) => `/api/v1/ai/company/${companyId}/analyze/result/${requestId}`,
      HEALTH: '/api/v1/ai/health'
    },
    UTILITIES: {
      SCRAPE: '/api/v1/utilities/scrape',
      VALIDATE_URL: '/api/v1/utilities/validate-url',
      ROBOTS_TXT: '/api/v1/utilities/robots-txt'
    },
    USER: {
      LANGUAGE_GET: '/api/v1/user/language',
      LANGUAGE_SET: '/api/v1/user/language'
    }
  },
  HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
};

export default API_CONFIG; 
